import { api } from '@general/ui-kit'

// Everything the mail client asks the platform: /mail/me/* — the signed-in
// person's own mail — plus the platform's /recipients (the address book) and
// /files (what they attach). No other module's API is called from here.

const BASE = '/mail/me'
const clean = (params = {}) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '' && value !== false)
)

export const mailApi = {
  overview: () => api.get(`${BASE}/overview`),
  unread: () => api.get(`${BASE}/unread`),

  accounts: () => api.get(`${BASE}/accounts`),
  createAccount: body => api.post(`${BASE}/accounts`, body),
  updateAccount: (id, body) => api.patch(`${BASE}/accounts/${id}`, body),
  removeAccount: id => api.delete(`${BASE}/accounts/${id}`),
  testAccount: body => api.post(`${BASE}/accounts/test`, body, { timeout: 60_000 }),
  sync: id => api.post(`${BASE}/accounts/${id}/sync`, {}, { timeout: 180_000 }),
  oauthStart: (kind, accountId) => api.get(`/mail/oauth/${kind}/start`, { params: clean({ accountId }) }),
  oauthFinish: (kind, { code, state }) => api.post(`/mail/oauth/${kind}/finish`, { code, state }),

  createFolder: (accountId, body) => api.post(`${BASE}/accounts/${accountId}/folders`, body),
  renameFolder: (accountId, body) => api.patch(`${BASE}/accounts/${accountId}/folders`, body),
  deleteFolder: (accountId, path) => api.delete(`${BASE}/accounts/${accountId}/folders`, { params: { path } }),

  labels: () => api.get(`${BASE}/labels`),
  createLabel: body => api.post(`${BASE}/labels`, body),
  updateLabel: (id, body) => api.patch(`${BASE}/labels/${id}`, body),
  removeLabel: id => api.delete(`${BASE}/labels/${id}`),

  list: params => api.get(`${BASE}/messages`, { params: clean(params) }),
  get: id => api.get(`${BASE}/messages/${id}`, { timeout: 60_000 }),
  act: body => api.post(`${BASE}/messages/actions`, body, { timeout: 60_000 }),
  link: (id, body) => api.post(`${BASE}/messages/${id}/links`, body),
  unlink: (id, linkId) => api.delete(`${BASE}/messages/${id}/links/${linkId}`),
  dismissSuggestion: (id, body) => api.post(`${BASE}/messages/${id}/suggestions/dismiss`, body),
  // Each attachment, linked on its own. They answer with the message's files
  // as they are now, so the screen redraws from one source.
  linkFile: (id, index, body) => api.post(`${BASE}/messages/${id}/files/${index}/links`, body, { timeout: 120_000 }),
  unlinkFile: (id, linkId) => api.delete(`${BASE}/messages/${id}/files/links/${linkId}`),
  dismissFileSuggestion: (id, index, body) => api.post(`${BASE}/messages/${id}/files/${index}/suggestions/dismiss`, body),
  analyseFiles: (id, { ai = false } = {}) => api.post(`${BASE}/messages/${id}/files/analyse`, { ai }, { timeout: 180_000 }),
  // The calendar, from a message: a draft for ITS editor, an invitation that
  // arrived, a meeting the text proposes, the meetings the thread is linked to.
  meetingDraft: (id, kind = 'meeting') => api.get(`${BASE}/messages/${id}/meeting-draft`, { params: { kind } }),
  meetingHint: id => api.get(`${BASE}/messages/${id}/meeting-hint`, { timeout: 60_000 }),
  dismissMeetingHint: id => api.post(`${BASE}/messages/${id}/meeting-hint/dismiss`, {}),
  respondInvitation: (id, body) => api.post(`${BASE}/messages/${id}/invitation/respond`, body, { timeout: 120_000 }),
  threadMeetings: id => api.get(`${BASE}/messages/${id}/meetings`),
  // A linked file opened from a record (not the owner's mailbox).
  linkedFile: linkId => api.get(`/mail/files/${linkId}`),
  linkedFileUrl: (linkId, { download = false } = {}) =>
    api.urlWithToken(`/mail/files/${linkId}/content${download ? '?download=1' : ''}`),
  attachmentUrl: (id, index, { download = false } = {}) =>
    api.urlWithToken(`${BASE}/messages/${id}/attachments/${index}${download ? '?download=1' : ''}`),

  context: (type, referenceId, options = null) => api.post(`${BASE}/compose/context`, { type, referenceId, options }, { timeout: 60_000 }),
  send: body => api.post(`${BASE}/send`, body, { timeout: 120_000 }),
  saveDraft: body => api.post(`${BASE}/drafts`, body, { timeout: 60_000 }),
  draft: id => api.get(`${BASE}/drafts/${id}`),
  ai: body => api.post(`${BASE}/ai`, body, { timeout: 120_000 }),

  recipients: params => api.get('/recipients', { params: clean(params) }),
  upload: (file) => {
    const form = new FormData()
    form.append('file', file)
    // Filed under the mail client: nobody reads it back through /files (its
    // default read permission, `mail:read`, belongs to no role) — only `send`
    // opens it, and only for the person who uploaded it.
    form.append('module', 'mail')
    return api.post('/files', form, { timeout: 180_000 })
  },
  removeUpload: id => api.delete(`/files/${id}`),
  relatedTypes: () => api.get('/references'),
}
